import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from "react-toastify";
import axiosInstance from '../../axiosInstance';

const OVERWRITEABLE_FIELDS = new Set([
  'customerCode',
  'lineName',
  'vnCode',
  'productName',
  'packageCount',
  'weight',
  'size',
  'priceInsurance',
  'shippingMethod',
  'note',
]);

const OVERWRITE_COLUMN_CONFIG = [
  { key: 'customerCode', label: 'Mã KH', width: '120px' },
  { key: 'lineName', label: 'Line', width: '160px' },
  { key: 'vnCode', label: 'Mã vận đơn VN', width: '160px' },
  { key: 'productName', label: 'Tên đơn hàng/sản phẩm', width: '220px' },
  { key: 'packageCount', label: 'Số kiện', width: '120px' },
  { key: 'weight', label: 'Khối lượng', width: '120px' },
  { key: 'size', label: 'Thể tích', width: '120px' },
  { key: 'priceInsurance', label: 'Giá trị b.hiểm', width: '140px' },
  { key: 'shippingMethod', label: 'PT lấy hàng', width: '160px' },
  { key: 'note', label: 'Ghi chú', width: '200px' },
];

const TABLE_COLUMN_CONFIG = [
  { key: 'rowSelection', width: '72px' },
  { key: 'cnCode', width: '150px' },
  ...OVERWRITE_COLUMN_CONFIG.map(({ key, width }) => ({ key, width })),
  { key: 'validation', width: '320px' },
];

const reviewCellStyle = {
  minWidth: '320px',
  width: '320px',
  verticalAlign: 'top',
};

const reviewMessageListStyle = {
  margin: 0,
  paddingLeft: '1rem',
  lineHeight: 1.45,
};

const reviewTableStyle = {
  minWidth: '1980px',
  tableLayout: 'fixed',
};

const checkboxCenterStyle = {
  width: '16px',
  height: '16px',
  cursor: 'pointer',
};

function normalizeCnCode(cnCode) {
  return String(cnCode ?? '').trim();
}

function hasDisplayValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function isOverwriteEligibleRow(row) {
  return Boolean(row?.overwriteEligible);
}

function collectEligibleOverwriteCnCodes(rows) {
  const set = new Set();
  (rows || []).forEach((row) => {
    if (isOverwriteEligibleRow(row) && normalizeCnCode(row.cnCode) !== '') {
      set.add(normalizeCnCode(row.cnCode));
    }
  });
  return set;
}

const OVERWRITE_FIELD_KEYS = OVERWRITE_COLUMN_CONFIG.map((column) => column.key);

function IndeterminateCheckbox({ checked, indeterminate = false, disabled = false, onChange, title }) {
  return (
    <input
      type="checkbox"
      className="form-check-input m-0"
      style={checkboxCenterStyle}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      title={title}
      ref={(input) => {
        if (input) {
          input.indeterminate = indeterminate;
        }
      }}
    />
  );
}

function getOverwriteSelectionClass(checked, partial = false) {
  if (checked) {
    return 'border-success bg-success-subtle shadow-sm text-success-emphasis';
  }
  if (partial) {
    return 'border-warning bg-warning-subtle shadow-sm text-warning-emphasis';
  }
  return 'border-secondary-subtle bg-body-tertiary text-dark';
}

const AddOrderByExcelModal = ({ show, handleClose, handleSaved }) => {
  const [excelImportFile, setExcelImportFile] = useState(null);
  const [excelReviewData, setExcelReviewData] = useState(null);
  const [isReviewingExcel, setIsReviewingExcel] = useState(false);
  const [isSavingOrders, setIsSavingOrders] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [overwriteSelections, setOverwriteSelections] = useState({});

  const handleModalClose = () => {
    setExcelImportFile(null);
    setExcelReviewData(null);
    setOverwriteSelections({});
    setIsReviewingExcel(false);
    setIsSavingOrders(false);
    handleClose();
  };

  const handleChangeExcelFile = (e) => {
    const selectedFile = e.target.files?.[0] || null;
    setExcelImportFile(selectedFile);
    setExcelReviewData(null);
    setOverwriteSelections({});
  };

  const handleReviewExcelFile = async () => {
    if (!excelImportFile) {
      toast.warning("Vui lòng chọn file Excel trước khi review");
      return;
    }

    setIsReviewingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', excelImportFile);
      const response = await axiosInstance.post('/api/import-orders/reviewOrdersByExcel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!response.data.success) {
        toast.error(response.data.message || "Review file thất bại");
        setExcelReviewData(response.data.data || null);
        setOverwriteSelections({});
        return;
      }

      setOverwriteSelections({});
      setExcelReviewData(response.data.data || null);
      toast.info(response.data.message || "Đã review file");
    } catch (error) {
      console.error("Review Excel error:", error);
      toast.error(error?.response?.data?.message || "Không thể review file Excel");
      setExcelReviewData(null);
    } finally {
      setIsReviewingExcel(false);
    }
  };

  const handleSaveOrdersByExcel = async () => {
    if (!excelImportFile) {
      toast.warning("Vui lòng chọn file Excel trước khi lưu");
      return;
    }
    if (!excelReviewData || !excelReviewData.canSave) {
      toast.warning("File có thông tin không đúng, vui lòng review và xử lý lỗi trước khi lưu");
      return;
    }

    const eligible = collectEligibleOverwriteCnCodes(excelReviewData.rows);
    const overwritePayload = Object.entries(overwriteSelections)
      .map(([cnCode, fieldMap]) => ({
        cnCode,
        fields: Object.entries(fieldMap || {})
          .filter(([, checked]) => checked)
          .map(([field]) => field)
          .filter((field) => OVERWRITEABLE_FIELDS.has(field)),
      }))
      .filter((item) => item.fields.length > 0 && eligible.has(item.cnCode));

    const hasValidRows = (excelReviewData.validRows ?? 0) > 0;
    if (!hasValidRows && overwritePayload.length === 0) {
      toast.warning("Chưa có ô nào được chọn để ghi đè.");
      return;
    }

    setIsSavingOrders(true);
    try {
      const formData = new FormData();
      formData.append('file', excelImportFile);
      formData.append('overwriteSelections', JSON.stringify(overwritePayload));

      const response = await axiosInstance.post('/api/import-orders/saveOrdersByExcel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!response.data.success) {
        toast.error(response.data.message || "Lưu đơn hàng thất bại");
        if (response.data.data) setExcelReviewData(response.data.data);
        return;
      }

      setExcelReviewData(response.data.data || excelReviewData);
      toast.success(response.data.message || "Lưu đơn hàng thành công");
      if (response.data.warning) {
        toast.warning(response.data.warning, { autoClose: 15000 });
      }
      if (handleSaved) handleSaved({ page: 0 });
      handleModalClose();
    } catch (error) {
      console.error("Save Orders By Excel error:", error);
      toast.error(error?.response?.data?.message || "Không thể lưu đơn hàng từ Excel");
    } finally {
      setIsSavingOrders(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const response = await axiosInstance.get('/api/import-orders/downloadOrdersByExcelTemplate', {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-tao-don-line-thuong-hang-loat.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download template error:", error);
      toast.error(error?.response?.data?.message || "Không thể tải template Excel");
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleToggleOverwriteField = (cnCode, field, checked) => {
    const cnKey = normalizeCnCode(cnCode);
    if (!cnKey || !OVERWRITEABLE_FIELDS.has(field)) return;

    setOverwriteSelections((prev) => {
      const next = { ...prev };
      const currentRow = { ...(next[cnKey] || {}) };

      if (checked) {
        currentRow[field] = true;
        next[cnKey] = currentRow;
        return next;
      }

      delete currentRow[field];
      if (Object.keys(currentRow).length === 0) {
        delete next[cnKey];
      } else {
        next[cnKey] = currentRow;
      }
      return next;
    });
  };

  const handleToggleOverwriteRow = (cnCode, checked) => {
    const cnKey = normalizeCnCode(cnCode);
    if (!cnKey) return;

    setOverwriteSelections((prev) => {
      const next = { ...prev };
      if (checked) {
        next[cnKey] = OVERWRITE_FIELD_KEYS.reduce((fieldMap, field) => {
          fieldMap[field] = true;
          return fieldMap;
        }, {});
        return next;
      }

      delete next[cnKey];
      return next;
    });
  };

  const handleToggleOverwriteColumn = (field, checked) => {
    if (!OVERWRITEABLE_FIELDS.has(field)) return;

    setOverwriteSelections((prev) => {
      const next = { ...prev };
      (excelReviewData?.rows || []).forEach((row) => {
        if (!isOverwriteEligibleRow(row)) return;

        const cnKey = normalizeCnCode(row.cnCode);
        if (!cnKey) return;

        const currentRow = { ...(next[cnKey] || {}) };
        if (checked) {
          currentRow[field] = true;
          next[cnKey] = currentRow;
          return;
        }

        delete currentRow[field];
        if (Object.keys(currentRow).length === 0) {
          delete next[cnKey];
        } else {
          next[cnKey] = currentRow;
        }
      });
      return next;
    });
  };

  const handleToggleAllOverwriteRows = (checked) => {
    if (!(excelReviewData?.rows || []).length) return;

    setOverwriteSelections((prev) => {
      const next = { ...prev };
      (excelReviewData.rows || []).forEach((row) => {
        if (!isOverwriteEligibleRow(row)) return;

        const cnKey = normalizeCnCode(row.cnCode);
        if (!cnKey) return;

        if (checked) {
          next[cnKey] = OVERWRITE_FIELD_KEYS.reduce((fieldMap, field) => {
            fieldMap[field] = true;
            return fieldMap;
          }, {});
          return;
        }

        delete next[cnKey];
      });
      return next;
    });
  };

  const isFieldChecked = (cnCode, field) => Boolean(overwriteSelections[normalizeCnCode(cnCode)]?.[field]);
  const isRowChecked = (row) => OVERWRITE_FIELD_KEYS.every((field) => isFieldChecked(row.cnCode, field));
  const isRowPartiallyChecked = (row) =>
    !isRowChecked(row) && OVERWRITE_FIELD_KEYS.some((field) => isFieldChecked(row.cnCode, field));
  const isColumnChecked = (field, eligibleRows) =>
    eligibleRows.length > 0 && eligibleRows.every((row) => isFieldChecked(row.cnCode, field));
  const isColumnPartiallyChecked = (field, eligibleRows) =>
    eligibleRows.some((row) => isFieldChecked(row.cnCode, field)) && !isColumnChecked(field, eligibleRows);

  const renderOverwriteCell = (row, field, value) => {
    const eligible = isOverwriteEligibleRow(row);
    const checked = eligible && isFieldChecked(row.cnCode, field);
    const hasValue = hasDisplayValue(value);

    if (!eligible) {
      return hasValue ? value : <span className="text-muted fst-italic">Trống</span>;
    }

    return (
      <button
        type="button"
        className={`btn d-flex flex-column justify-content-between gap-2 rounded-3 border p-2 h-100 w-100 text-start ${getOverwriteSelectionClass(checked)}`}
        onClick={() => handleToggleOverwriteField(row.cnCode, field, !checked)}
        title={`Chọn ghi đè ${field}`}
      >
        <div className={`${hasValue ? 'fw-medium' : 'text-muted fst-italic'} text-break`} style={{ lineHeight: 1.35 }}>
          {hasValue ? value : 'Trống'}
        </div>
      </button>
    );
  };

  const renderOverwriteHeaderCell = (column, eligibleRows) => {
    const checked = isColumnChecked(column.key, eligibleRows);
    const partial = isColumnPartiallyChecked(column.key, eligibleRows);

    return (
      <button
        type="button"
        className={`btn d-flex flex-column align-items-center justify-content-center rounded-3 border p-2 h-100 w-100 text-center ${getOverwriteSelectionClass(checked, partial)} ${eligibleRows.length > 0 ? 'cursor-pointer' : ''}`}
        title={`Chọn toàn bộ cột ${column.label}`}
        disabled={eligibleRows.length === 0}
        onClick={() => handleToggleOverwriteColumn(column.key, !checked)}
      >
        <span className="fw-semibold">{column.label}</span>
      </button>
    );
  };

  const rows = excelReviewData?.rows || [];
  const globalErrors = excelReviewData?.globalErrors || [];
  const eligibleOverwriteRows = rows.filter((row) => isOverwriteEligibleRow(row));
  const selectedOverwriteCount = Object.values(overwriteSelections).reduce(
    (total, fieldMap) => total + Object.values(fieldMap || {}).filter(Boolean).length,
    0,
  );
  const hasValidRows = (excelReviewData?.validRows ?? 0) > 0;
  const canSubmit = Boolean(excelReviewData?.canSave) && (hasValidRows || selectedOverwriteCount > 0);
  const areAllOverwriteRowsChecked = eligibleOverwriteRows.length > 0 && eligibleOverwriteRows.every(isRowChecked);
  const areSomeOverwriteRowsChecked =
    eligibleOverwriteRows.some((row) => isRowPartiallyChecked(row) || isRowChecked(row)) && !areAllOverwriteRowsChecked;

  return (
    <Modal show={show} onHide={handleModalClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Import đơn hàng bằng file Excel</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="card mb-3">
          <div className="card-header fw-bold">Chọn file</div>
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-md-6">
                <Form.Group controlId="orderExcelFile">
                  <Form.Label>File Excel</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleChangeExcelFile}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3 d-grid">
                <Button
                  variant="primary"
                  onClick={handleReviewExcelFile}
                  disabled={!excelImportFile || isReviewingExcel || isSavingOrders}
                >
                  {isReviewingExcel ? "Đang kiểm tra..." : "Kiểm tra"}
                </Button>
              </div>
              <div className="col-md-3 d-grid">
                <Button
                  variant="outline-secondary"
                  onClick={handleDownloadTemplate}
                  disabled={isDownloadingTemplate || isReviewingExcel || isSavingOrders}
                >
                  {isDownloadingTemplate ? "Đang tải..." : "Download template Excel"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header fw-bold">Kiểm tra và xác nhận</div>
          <div className="card-body p-0">
            {excelReviewData && (
              <div className="p-3 border-bottom bg-light">
                <div className="row g-2">
                  <div className="col-md-3"><b>Tổng dòng:</b> {excelReviewData.totalRows ?? 0}</div>
                  <div className="col-md-3 text-success"><b>Hợp lệ:</b> {excelReviewData.validRows ?? 0}</div>
                  <div className="col-md-3 text-danger"><b>Lỗi:</b> {excelReviewData.invalidRows ?? 0}</div>
                  <div className="col-md-3"><b>Có thể lưu:</b> {excelReviewData.canSave ? "Có" : "Không"}</div>
                  <div className="col-md-6"><b>Dòng có thể ghi đè:</b> {eligibleOverwriteRows.length}</div>
                  <div className="col-md-6"><b>Số trường đã chọn ghi đè:</b> {selectedOverwriteCount}</div>
                </div>

                {globalErrors.length > 0 && (
                  <div className="mt-2 text-danger">
                    {globalErrors.map((error, index) => (
                      <div key={index}>- {error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {rows.length === 0 ? (
              <div className="p-3 text-muted">Chưa có dữ liệu line để hiển thị.</div>
            ) : (
              <div className="table-responsive" style={{ maxHeight: '420px' }}>
                <table className="table table-sm table-bordered mb-0 align-middle" style={reviewTableStyle}>
                  <colgroup>
                    {TABLE_COLUMN_CONFIG.map((column) => (
                      <col key={column.key} style={{ width: column.width, minWidth: column.width }} />
                    ))}
                  </colgroup>
                  <thead className="table-light position-sticky top-0">
                    <tr>
                      <th className="text-center align-middle">
                        <div className="d-flex flex-column align-items-center justify-content-center">
                          <IndeterminateCheckbox
                            checked={areAllOverwriteRowsChecked}
                            indeterminate={areSomeOverwriteRowsChecked}
                            disabled={eligibleOverwriteRows.length === 0}
                            onChange={(e) => handleToggleAllOverwriteRows(e.target.checked)}
                            title="Chọn toàn bộ các dòng có thể ghi đè"
                          />
                        </div>
                      </th>
                      <th className="text-center">Mã vận đơn TQ</th>
                      {OVERWRITE_COLUMN_CONFIG.map((column) => (
                        <th key={column.key} className="align-middle p-1">
                          {renderOverwriteHeaderCell(column, eligibleOverwriteRows)}
                        </th>
                      ))}
                      <th>Kiểm tra</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={index} className={row.valid ? "" : isOverwriteEligibleRow(row) ? "table-warning" : "table-danger"}>
                        <td className="text-center align-middle">
                          {isOverwriteEligibleRow(row) ? (
                            <IndeterminateCheckbox
                              checked={isRowChecked(row)}
                              indeterminate={isRowPartiallyChecked(row)}
                              onChange={(e) => handleToggleOverwriteRow(row.cnCode, e.target.checked)}
                              title={`Chọn toàn bộ dòng ${row.rowNumber ?? index + 1}`}
                            />
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center">{hasDisplayValue(row.cnCode) ? row.cnCode : <span className="text-muted fst-italic">Trống</span>}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'customerCode', row.customerCode)}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'lineName', row.lineName)}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'vnCode', row.vnCode)}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'productName', row.productName)}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'packageCount', row.packageCount ?? '')}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'weight', row.weight ?? '')}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'size', row.size ?? '')}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'priceInsurance', row.priceInsurance ?? '')}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'shippingMethod', row.shippingMethod)}</td>
                        <td className="p-1">{renderOverwriteCell(row, 'note', row.noteAdmin || row.noteUser)}</td>
                        <td className="p-2" style={reviewCellStyle}>
                          {row.valid ? (
                            <span className="text-success">Hợp lệ</span>
                          ) : (
                            <div className="text-danger" style={{ display: 'block' }}>
                              <ul style={reviewMessageListStyle}>
                                {(row.errors || []).map((error, errorIndex) => (
                                  <li key={errorIndex}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="success"
          onClick={handleSaveOrdersByExcel}
          disabled={!canSubmit || isSavingOrders || isReviewingExcel}
        >
          {isSavingOrders ? "Đang lưu..." : "Lưu đơn hàng"}
        </Button>
        <Button variant="secondary" onClick={handleModalClose}>
          Đóng
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddOrderByExcelModal;
